import { memo } from 'react'
import { useL10n } from '../hooks/useL10n'
import './StageCard.css'

export type StageCardStatus =
  | 'available'
  | 'completed'
  | 'locked'
  | 'special-boss'
  | 'special-rhythm'
  | 'special-training'
  | 'special-survival'

export interface StageCardProps {
  id: string
  title: string
  subtitle?: string
  backgroundUrl: string
  status?: StageCardStatus
  selected?: boolean
  badge?: string
  onSelect: (id: string) => void
}

function StageCardImpl({
  id,
  title,
  subtitle,
  backgroundUrl,
  status = 'available',
  selected = false,
  badge,
  onSelect,
}: StageCardProps) {
  const { t } = useL10n()
  const locked = status === 'locked'
  const completed = status === 'completed'
  const isBoss = status === 'special-boss'
  const isRhythm = status === 'special-rhythm'
  const isTraining = status === 'special-training'
  const isSurvival = status === 'special-survival'

  const badgeText =
    badge ??
    (isBoss
      ? t('ui.badge_boss')
      : isRhythm
        ? t('ui.badge_rhythm')
        : isTraining
          ? t('ui.badge_training')
          : isSurvival
            ? t('ui.badge_survival')
            : completed
              ? t('ui.badge_completed')
              : null)

  const badgeClass =
    'stage-card__badge' +
    (isBoss ? ' stage-card__badge--boss' : '') +
    (isRhythm ? ' stage-card__badge--rhythm' : '') +
    (isTraining ? ' stage-card__badge--training' : '') +
    (isSurvival ? ' stage-card__badge--survival' : '') +
    (completed ? ' stage-card__badge--completed' : '')

  return (
    <button
      type="button"
      className="stage-card"
      data-status={status}
      data-selected={selected || undefined}
      style={{ backgroundImage: `url(${backgroundUrl})` }}
      onClick={() => !locked && onSelect(id)}
      disabled={locked}
      aria-pressed={selected}
      aria-label={subtitle ? `${title} — ${subtitle}` : title}
    >
      {badgeText && (
        <div className="stage-card__badges">
          <span className={badgeClass}>{badgeText}</span>
        </div>
      )}
      <div className="stage-card__bottom">
        <h3 className="stage-card__title">{title}</h3>
        {subtitle && <div className="stage-card__subtitle">{subtitle}</div>}
      </div>
    </button>
  )
}

export const StageCard = memo(StageCardImpl)
